import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import clsx from 'clsx';

import { calcSeatFare } from 'helpers/ticketSales';
import { currencyFormat } from 'helpers/currencyFormat';

import { SEAT_POSITION } from 'constants/ticketSales';
import IMAGES from 'constants/images';

import { Arrow2Left, Arrow2Right } from 'icons/Arrows';
import { Close } from 'icons/Tools_Interface';

import { ItineraryListInterface } from 'store/ducks/publicStore/types';
import { TicktDataInterface } from 'store/ducks/publicStore/types';
import { SEAT_TYPE } from '../..';
import { setTicketDataRequest } from 'store/ducks/publicStore/actions';

interface SeatEntry {
  seatNumber: number;
  value: number;
  id: string;
  floor: number;
  type: number;
}

interface SeatMapProps {
  trip: ItineraryListInterface;
  selectionKey: string;
  seatField: { [tripScheduleId: string]: SeatEntry[] };
  setSeatField: React.Dispatch<
    React.SetStateAction<{ [tripScheduleId: string]: SeatEntry[] }>
  >;
  ocupedSeat: { [tripScheduleId: string]: string[] };
  floor: number;
  setFloor: (floor: number) => void;
  XL: boolean;
  SM: boolean;
  MD: boolean;
  LG: boolean;
  ticketData: TicktDataInterface;
  classes: Record<string, string>;
}

const SeatMap: React.FC<SeatMapProps> = ({
  trip,
  selectionKey,
  seatField,
  setSeatField,
  ocupedSeat,
  floor,
  setFloor,
  XL,
  SM,
  MD,
  LG,
  ticketData,
  classes,
}) => {
  const tripId = trip.tripScheduleId;
  const selectedSeatType = trip.seats?.find((seat) => seat.enabled)?.seatType;
  const [firstFill, setFirstFill] = useState(false);

  const seatButton = (
    enabled: boolean,
    seatNumber: number,
    id: string,
    floor: number,
    type: number,
  ) => (
    <Tooltip title={SEAT_TYPE[type as keyof typeof SEAT_TYPE]?.LABEL}>
      <Button
        disabled={
          !enabled ||
          ocupedSeat?.[selectionKey]?.includes(id) ||
          (seatField?.[selectionKey] || []).filter(
            (item) => item.floor === floor,
          ).length >= 10
        }
        className={clsx(classes.seat, {
          [classes.selected]:
            seatField?.[selectionKey]?.find((item) => item.id === id) &&
            !ocupedSeat?.[selectionKey]?.includes(id),
          [classes.notAvaliable]:
            !enabled || ocupedSeat?.[selectionKey]?.includes(id),
          [classes.xl]: XL,
          [classes.md]: MD,
        })}
        onClick={() => {
          const currentSeats = seatField?.[selectionKey] || [];
          const found = currentSeats.find((item) => item.id === id);
          const update = found
            ? currentSeats.filter((item) => item.id !== id)
            : [
                ...currentSeats,
                {
                  seatNumber,
                  value: calcSeatFare(trip, type),
                  id,
                  floor,
                  type,
                },
              ];
          setSeatField((s) => ({ ...s, [selectionKey]: update }));
        }}
        key={seatNumber}>
        {seatNumber}
      </Button>
    </Tooltip>
  );

  const seatsByPosition = (position: number) =>
    trip.seats
      .filter((e) => e.floor === floor)
      .filter((seat) => seat.position === position)
      .sort((a, b) => a.seatNumber - b.seatNumber)
      .map(({ seatNumber, enabled, id, floor: f, seatType }) =>
        seatButton(enabled, seatNumber, id, f, seatType),
      );
  useEffect(() => {
    if (!firstFill) {
      setSeatField((s) => ({
        ...s,
        [selectionKey]:
          ticketData.selectedTravelGo?.holdTokens
            ?.filter(
              (item) =>
                item.tripScheduleId === tripId &&
                (selectedSeatType === undefined ||
                  item.type === selectedSeatType),
            )
            .map((item) => ({
              seatNumber: item.seatNumber || 0,
              value: item.value || 0,
              id: item.tripScheduleSeatId || '',
              floor: item.floor || 1,
              type: item.type,
            })) || [],
      }));
    }
    setFirstFill(true);
  }, [
    ticketData,
    firstFill,
    selectionKey,
    selectedSeatType,
    tripId,
    setSeatField,
  ]);

  return (
    <>
      <Box m='0.75rem 0'>
        <Divider />
      </Box>
      <Grid container xl={12} lg={12} md={12} sm={12} xs={12}>
        {/* Mapa de assentos */}
        <Grid
          alignItems='center'
          container
          xl={9}
          lg={LG ? 12 : 9}
          md={12}
          sm={12}
          xs={12}>
          <Box className={classes.horizontalBusPlan}>
            <img
              src={
                SM
                  ? IMAGES.BACKGROUND_IMAGES.VERTICAL_BUS_PLAN
                  : IMAGES.BACKGROUND_IMAGES.HORIZONTAL_BUS_PLAN
              }
            />
            <Box
              top='2rem'
              className={clsx(classes.busPlanContent, {
                [classes.firstBusPlanContentSm]: SM,
                [classes.firstBusPlanContentXl]: XL,
              })}>
              <Box
                display='flex'
                alignItems='center'
                flexDirection={SM ? 'column' : 'row'}>
                {seatsByPosition(SEAT_POSITION.WINDOW_RIGHT)}
              </Box>
              <Box
                display='flex'
                alignItems='center'
                flexDirection={SM ? 'column' : 'row'}>
                {seatsByPosition(SEAT_POSITION.CORRIDOR_RIGHT)}
              </Box>
            </Box>
            <Box
              bottom='2rem'
              className={clsx(classes.busPlanContent, {
                [classes.secondBusPlanContentSm]: SM,
                [classes.secondBusPlanContentXl]: XL,
              })}>
              <Box
                display='flex'
                alignItems='center'
                flexDirection={SM ? 'column' : 'row'}>
                {seatsByPosition(SEAT_POSITION.CORRIDOR_LEFT)}
              </Box>
              <Box
                display='flex'
                alignItems='center'
                flexDirection={SM ? 'column' : 'row'}>
                {seatsByPosition(SEAT_POSITION.WINDOW_LEFT)}
              </Box>
            </Box>
          </Box>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            width='100%'
            mt='1rem'>
            <Button disabled={floor === 1} onClick={() => setFloor(1)}>
              <Arrow2Left />
            </Button>
            <Typography style={{ margin: '0.5rem 0' }}>{floor}</Typography>
            <Button disabled={floor === 2} onClick={() => setFloor(2)}>
              <Arrow2Right />
            </Button>
          </Box>
        </Grid>

        {/* Lista de assentos selecionados */}
        <Grid
          style={{
            minWidth: '14rem',
            paddingTop: '1.75rem',
            justifyContent: 'center',
          }}
          alignItems='baseline'
          container
          xl={3}
          lg={LG ? 2 : 3}
          md={12}
          sm={12}
          xs={12}>
          <Box width='14rem'>
            <Typography style={{ fontWeight: 500 }}>
              Escolha até 5 assentos
            </Typography>
            {seatField?.[selectionKey]?.map(({ seatNumber, type }) => (
              <Box
                key={seatNumber}
                display='flex'
                alignItems='center'
                mb='0.5rem'>
                <Box
                  borderRadius='4px'
                  bgcolor='#0094FF'
                  mr='0.5rem'
                  width='1rem'
                  height='1rem'
                />
                <Typography style={{ marginRight: '1.5rem' }}>
                  Assento {seatNumber}
                </Typography>
                <Typography style={{ marginRight: '0.5rem' }}>
                  {currencyFormat({
                    value: calcSeatFare(trip, type),
                  })}
                </Typography>
                <IconButton
                  onClick={() => {
                    const update = (seatField[selectionKey] || []).filter(
                      (item) =>
                        !(
                          item.seatNumber === seatNumber && item.floor === floor
                        ),
                    );
                    setSeatField((s) => ({ ...s, [selectionKey]: update }));
                  }}
                  style={{
                    background: '#E22020',
                    width: '1.25rem',
                    height: '1.25rem',
                    padding: '4px',
                    color: '#fff',
                  }}>
                  <Close width='0.75rem' height='0.75rem' fill='#fff' />
                </IconButton>
              </Box>
            ))}

            {seatField?.[selectionKey]?.length > 1 && (
              <Box>
                <Box bgcolor='#000' width='100%' height='1px' m='2px 0' />
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'>
                  <Typography style={{ marginRight: '1.5rem' }}>
                    {seatField?.[selectionKey]?.length} assentos
                  </Typography>
                  <Typography style={{ marginRight: '1.625rem' }}>
                    {currencyFormat({
                      value: (seatField[selectionKey] || []).reduce(
                        (acc, item) => acc + calcSeatFare(trip, item.type),
                        0,
                      ),
                    })}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      <Box m='0.75rem 0'>
        <Divider />
      </Box>
    </>
  );
};

export default SeatMap;
